import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  Tabs,
  Tree,
  Input,
  Button,
  Select,
  Card,
  Row,
  Col,
  Space,
  Tag,
  Image,
  Empty,
  Tooltip,
  message,
  Spin,
  Dropdown,
  Menu,
} from 'antd';
import {
  FileTextOutlined,
  FileImageOutlined,
  VideoCameraOutlined,
  LinkOutlined,
  AppstoreOutlined,
  FolderOutlined,
  PlayCircleOutlined
} from '@ant-design/icons';
import api from '../services/api';
import './MaterialSelector.css';

const { Search } = Input;
const { Option } = Select;
const { TabPane } = Tabs;

const MaterialSelector = ({
  visible,
  onCancel,
  onSelect,
  materialType = null, // 限制素材类型
  hideAllTypesTab = false, // 是否隐藏"全部类型"标签页
  multiSelect = false, // 是否支持多选
  selectedMaterials = [], // 已选中的素材
  title = '选择素材',
  allowUpload = true, // 是否允许上传新素材
  onUpload = null, // 上传按钮点击回调
  refreshTrigger = 0, // 刷新触发器，当这个值变化时会刷新列表
  tenantId
}) => {
  // 状态管理
  const [activeTab, setActiveTab] = useState('library');
  const [groupTree, setGroupTree] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState(() => {
    try {
      const savedGroupId = localStorage.getItem('materials_last_selected_group');
      if (savedGroupId) {
        const groupId = parseInt(savedGroupId);
        return isNaN(groupId) ? -1 : groupId;
      }
    } catch (error) {
      console.warn('读取上次选中的分组失败:', error);
    }
    return -1;
  });
  const [selectedMaterialType, setSelectedMaterialType] = useState(materialType);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedItems, setSelectedItems] = useState(selectedMaterials);
  const [selectedOrder, setSelectedOrder] = useState({}); // 记录选择顺序
  const [isImageMultiSelectMode, setIsImageMultiSelectMode] = useState(false); // 是否处于图片多选模式
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 12,
    total: 0
  });



  // 素材类型配置
  const materialTypes = [
    { value: null, label: '全部类型', icon: <AppstoreOutlined /> },
    { value: 'TEXT', label: '文本', icon: <FileTextOutlined /> },
    { value: 'IMAGE', label: '图片', icon: <FileImageOutlined /> },
    { value: 'VIDEO', label: '视频', icon: <VideoCameraOutlined /> },
    { value: 'MINIPROGRAM', label: '小程序', icon: <AppstoreOutlined /> },
    { value: 'LINK', label: '网页', icon: <LinkOutlined /> }
  ];

  // 获取OSS预览URL
  const getOssPreviewUrl = async (ossUrl) => {
    if (!ossUrl) return null;
    
    // 如果已经是HTTP URL，直接返回
    if (ossUrl.startsWith('http')) {
      return ossUrl;
    }
    
    // 如果是OSS协议URL，请求转换
    if (ossUrl.startsWith('oss://')) {
      try {
        const response = await api.get(`/media/wework/oss/preview?url=${encodeURIComponent(ossUrl)}`);
        const data = response.data;
        
        if (data.success && data.url) {
          return data.url;
        }
      } catch (error) {
        console.error('获取预览URL失败:', error);
      }
    }
    
    return ossUrl; // 后备方案，返回原URL
  };

  // 批量处理素材预览URL
  const processMaterialPreviewUrls = async (materialsData) => {
    const processedMaterials = [];
    
    for (const material of materialsData) {
      const processedMaterial = { ...material };
      
      try {
        // 处理图片素材
        if (material.materialType === 'IMAGE' && material.imageInfo?.url) {
          const previewUrl = await getOssPreviewUrl(material.imageInfo.url);
          processedMaterial.imageInfo = {
            ...material.imageInfo,
            previewUrl: previewUrl
          };
        }
        
        // 处理视频素材
        if (material.materialType === 'VIDEO' && material.videoInfo?.url) {
          const previewUrl = await getOssPreviewUrl(material.videoInfo.url);
          processedMaterial.videoInfo = {
            ...material.videoInfo,
            previewUrl: previewUrl
          };
          
          // 对于视频，尝试生成缩略图URL（如果有的话）
          if (material.videoInfo.thumbnailUrl) {
            const thumbnailPreviewUrl = await getOssPreviewUrl(material.videoInfo.thumbnailUrl);
            processedMaterial.videoInfo.thumbnailPreviewUrl = thumbnailPreviewUrl;
          }
        }
        
        // 处理链接素材的封面图
        if (material.materialType === 'LINK' && material.linkInfo?.picUrl) {
          const previewUrl = await getOssPreviewUrl(material.linkInfo.picUrl);
          processedMaterial.linkInfo = {
            ...material.linkInfo,
            picPreviewUrl: previewUrl
          };
        }
        
        // 处理小程序素材的封面图
        if (material.materialType === 'MINIPROGRAM' && material.miniprogramInfo?.picUrl) {
          const previewUrl = await getOssPreviewUrl(material.miniprogramInfo.picUrl);
          processedMaterial.miniprogramInfo = {
            ...material.miniprogramInfo,
            picPreviewUrl: previewUrl
          };
        }
      } catch (error) {
        console.error('处理素材预览URL失败:', error);
      }
      
      processedMaterials.push(processedMaterial);
    }
    
    return processedMaterials;
  };

  // 过滤素材类型选项
  const getFilteredMaterialTypes = () => {
    let filteredTypes = materialTypes;
    
    // 如果需要隐藏"全部类型"标签页，先过滤掉value为null的项
    if (hideAllTypesTab) {
      filteredTypes = materialTypes.filter(type => type.value !== null);
    }
    
    // 根据materialType进一步过滤
    if (materialType) {
      filteredTypes = filteredTypes.filter(type => type.value === materialType);
    }
    
    return filteredTypes;
  };

  // 加载分组树
  const loadGroupTree = useCallback(async () => {
    if (!tenantId) {
      console.warn('未提供tenantId，无法加载分组');
      return;
    }
    
    try {
      const response = await api.get('/material-groups/tree', {
        params: { tenantId }
      });
      if (response.data.success) {
        setGroupTree(convertToTreeData(response.data.data));
      }
    } catch (error) {
      console.error('加载分组失败:', error);
    }
  }, [tenantId]);

  // 转换分组数据为Tree组件格式
  const convertToTreeData = (groups) => {
    return groups.map(group => ({
      key: group.id,
      title: group.name,
      icon: <FolderOutlined />,
      children: group.children ? convertToTreeData(group.children) : []
    }));
  };

  // 加载素材列表（可指定materialType）
  const loadMaterialsWithType = useCallback(async (page = 1, materialTypeParam = null) => {
    if (!tenantId) {
      console.warn('未提供tenantId，无法加载素材');
      return;
    }
    
    setLoading(true);
    try {
      const params = {
        tenantId,
        materialType: materialTypeParam !== null ? materialTypeParam : selectedMaterialType,
        keyword: searchKeyword,
        page: page - 1,
        size: pagination.pageSize
      };

      console.log('MaterialSelector - loadMaterialsWithType API参数:', params);

      // 处理分组ID（和Materials.js保持一致）
      if (selectedGroupId === -1) {
        // 选中了"全部"，显示全部素材，不传递任何分组参数
        console.log('MaterialSelector - 选中全部，不传递分组参数');
      } else if (selectedGroupId === null) {
        // 选中了"未分组"，只显示未分组的素材
        params.ungrouped = true;
        console.log('MaterialSelector - 选中未分组，设置ungrouped=true');
      } else {
        // 选中了具体分组
        params.groupId = selectedGroupId;
        console.log('MaterialSelector - 选中具体分组:', selectedGroupId);
      }

      const response = await api.get('/materials', { params });
      
      if (response.data.success) {
        // 处理预览URL
        const processedMaterials = await processMaterialPreviewUrls(response.data.data.content);
        setMaterials(processedMaterials);
        setPagination({
          ...pagination,
          current: page,
          total: response.data.data.totalElements
        });
      }
    } catch (error) {
      message.error('加载素材失败');
      console.error('加载素材失败:', error);
    } finally {
      setLoading(false);
    }
  }, [tenantId, selectedGroupId, selectedMaterialType, searchKeyword, pagination.pageSize]);

  // 加载素材列表
  const loadMaterials = useCallback(async (page = 1) => {
    return loadMaterialsWithType(page, null);
  }, [loadMaterialsWithType]);

  // 初始化加载：当Modal打开时，加载分组树并设置materialType
  useEffect(() => {
    if (visible) {
      console.log('MaterialSelector - Modal打开，传入的materialType:', materialType);
      loadGroupTree();
      setSelectedMaterialType(materialType);
      console.log('MaterialSelector - 设置selectedMaterialType为:', materialType);
      // 重置选择状态
      setSelectedItems(selectedMaterials);
      setSelectedOrder({});
      setIsImageMultiSelectMode(false);
      // 延迟加载素材，确保状态设置完成
      setTimeout(() => {
        if (tenantId) {
          console.log('MaterialSelector - 延迟加载素材，使用materialType:', materialType);
          // 直接调用加载函数，传入正确的materialType
          loadMaterialsWithType(1, materialType);
        }
      }, 100);
    }
  }, [visible, materialType]);

  // 监听筛选条件变化 - 只监听分组和搜索词变化，不监听visible避免重复
  useEffect(() => {
    if (visible && tenantId) {
      // 使用loadMaterialsWithType确保传递正确的materialType参数
      // 优先使用传入的materialType，如果没有则使用selectedMaterialType
      const effectiveMaterialType = materialType !== undefined ? materialType : selectedMaterialType;
      console.log('MaterialSelector - 筛选条件变化，使用materialType:', effectiveMaterialType);
      loadMaterialsWithType(1, effectiveMaterialType);
    }
  }, [selectedGroupId, searchKeyword, visible, tenantId]);

  // 单独监听materialType变化 - 确保类型筛选生效
  useEffect(() => {
    if (visible && tenantId) {
      console.log('MaterialSelector - selectedMaterialType变化，重新加载素材:', selectedMaterialType);
      // 使用loadMaterialsWithType确保传递正确的materialType参数
      // 优先使用传入的materialType，如果没有则使用selectedMaterialType
      const effectiveMaterialType = materialType !== undefined ? materialType : selectedMaterialType;
      loadMaterialsWithType(1, effectiveMaterialType);
    }
  }, [selectedMaterialType, visible, tenantId, materialType]);

  // 监听refreshTrigger变化 - 用于外部触发刷新
  useEffect(() => {
    if (visible && tenantId && refreshTrigger > 0) {
      console.log('MaterialSelector - refreshTrigger变化，刷新素材列表:', refreshTrigger);
      // 使用loadMaterialsWithType确保传递正确的materialType参数
      // 优先使用传入的materialType，如果没有则使用selectedMaterialType
      const effectiveMaterialType = materialType !== undefined ? materialType : selectedMaterialType;
      loadMaterialsWithType(1, effectiveMaterialType);
    }
  }, [refreshTrigger, visible, tenantId, loadMaterialsWithType, materialType, selectedMaterialType]);


  // 分组选择
  const handleGroupSelect = (selectedKeys) => {
    const selectedKey = selectedKeys[0];
    console.log('MaterialSelector - Tree选中的key:', selectedKey, '类型:', typeof selectedKey);
    
    // 处理Tree组件的key转换（和Materials.js保持一致）：
    // -1: 全部
    // "0-1" 或类似: Tree组件对null的字符串化表示，转换回null表示未分组
    // 数字字符串: 具体分组ID，需要转换为数字
    
    let actualGroupId;
    if (selectedKey === -1 || selectedKey === '-1') {
      actualGroupId = -1; // 全部
    } else if (selectedKey === null || selectedKey === 'null' || typeof selectedKey === 'string' && selectedKey.includes('-')) {
      actualGroupId = null; // 未分组（Tree组件可能把null转换成各种字符串形式）
    } else {
      // 具体分组ID
      actualGroupId = typeof selectedKey === 'string' ? parseInt(selectedKey) : selectedKey;
    }
    
    console.log('MaterialSelector - 处理后的groupId:', actualGroupId);
    setSelectedGroupId(actualGroupId);
    
    // 保存选中的分组到localStorage
    try {
      if (actualGroupId !== null && actualGroupId !== -1) {
        localStorage.setItem('materials_last_selected_group', actualGroupId.toString());
      } else {
        localStorage.removeItem('materials_last_selected_group');
      }
    } catch (error) {
      console.warn('保存选中分组失败:', error);
    }
  };

  // 搜索
  const handleSearch = (value) => {
    setSearchKeyword(value);
  };


  // 素材选择
  const handleMaterialSelect = (material) => {
    const isSelected = selectedItems.some(item => item.id === material.id);
    
    // 如果当前素材已选中，取消选择
    if (isSelected) {
      const newSelectedItems = selectedItems.filter(item => item.id !== material.id);
      setSelectedItems(newSelectedItems);
      
      // 更新选择顺序
      const newSelectedOrder = { ...selectedOrder };
      delete newSelectedOrder[material.id];
      
      // 重新排序剩余项目的顺序号
      const remainingItems = newSelectedItems.map(item => item.id);
      const newOrder = {};
      remainingItems.forEach((id, index) => {
        newOrder[id] = index + 1;
      });
      setSelectedOrder(newOrder);
      
      // 如果没有选中项，退出多选模式
      if (newSelectedItems.length === 0) {
        setIsImageMultiSelectMode(false);
      }
      return;
    }
    
    // 如果是图片类型且支持多选
    if (material.materialType === 'IMAGE' && multiSelect) {
      // 如果当前不在图片多选模式，且已经选择了非图片素材，不允许选择
      if (!isImageMultiSelectMode && selectedItems.length > 0 && selectedItems.some(item => item.materialType !== 'IMAGE')) {
        message.warning('已选择其他类型素材，无法添加图片素材');
        return;
      }
      
      // 进入或保持图片多选模式
      setIsImageMultiSelectMode(true);
      
      const newSelectedItems = [...selectedItems, material];
      setSelectedItems(newSelectedItems);
      
      // 设置选择顺序
      const newOrder = { ...selectedOrder };
      newOrder[material.id] = newSelectedItems.length;
      setSelectedOrder(newOrder);
    } else {
      // 非图片类型或不支持多选
      if (multiSelect) {
        // 如果当前在图片多选模式，不允许选择非图片素材
        if (isImageMultiSelectMode) {
          message.warning('已选择图片素材，无法添加其他类型素材');
          return;
        }
        
        // 如果已经选择了其他素材，不允许多选非图片素材
        if (selectedItems.length > 0) {
          message.warning('非图片素材只能单独选择');
          return;
        }
      }
      
      // 单选模式或非图片类型的选择
      setSelectedItems([material]);
      setSelectedOrder({ [material.id]: 1 });
      setIsImageMultiSelectMode(false);
    }
  };

  // 确认选择
  const handleConfirm = () => {
    if (selectedItems.length === 0) {
      message.warning('请选择素材');
      return;
    }
    
    // 增加素材使用次数（使用专门的计数端点，避免不必要的格式转换）
    selectedItems.forEach(material => {
      api.post(`/materials/${material.id}/increment-usage`).catch(console.error);
    });
    
    // 如果是多选模式，按选择顺序排序
    if (multiSelect && selectedItems.length > 1) {
      const sortedItems = [...selectedItems].sort((a, b) => {
        const orderA = selectedOrder[a.id] || 0;
        const orderB = selectedOrder[b.id] || 0;
        return orderA - orderB;
      });
      onSelect(sortedItems);
    } else {
      onSelect(multiSelect ? selectedItems : selectedItems[0]);
    }
  };

  // 获取素材类型图标
  const getMaterialTypeIcon = (type) => {
    const typeConfig = materialTypes.find(t => t.value === type);
    return typeConfig ? typeConfig.icon : <FileTextOutlined />;
  };

  // 获取素材类型标签
  const getMaterialTypeTag = (type) => {
    const typeConfig = materialTypes.find(t => t.value === type);
    const colors = {
      TEXT: 'blue',
      IMAGE: 'green',
      VIDEO: 'purple',
      LINK: 'orange',
      MINIPROGRAM: 'red'
    };
    return (
      <Tag color={colors[type]} size="small">
        {typeConfig ? typeConfig.label : type}
      </Tag>
    );
  };

  // 获取上传按钮文字
  const getUploadButtonText = () => {
    const currentType = selectedMaterialType || materialType;
    if (!currentType) {
      return '上传素材';
    }
    const typeConfig = materialTypes.find(t => t.value === currentType);
    return `上传${typeConfig ? typeConfig.label : '素材'}`;
  };

  // 渲染素材卡片
  const renderMaterialCard = (material) => {
    const isSelected = selectedItems.some(item => item.id === material.id);
    const selectionOrder = selectedOrder[material.id] || 0;
    
    // 判断当前素材是否可选
    const isSelectable = () => {
      if (isSelected) return true; // 已选中的素材总是可以点击（用于取消选择）
      
      if (!multiSelect) return true; // 非多选模式下所有素材都可选
      
      if (selectedItems.length === 0) return true; // 没有选中任何素材时都可选
      
      // 如果当前处于图片多选模式
      if (isImageMultiSelectMode) {
        return material.materialType === 'IMAGE'; // 只有图片可选
      }
      
      // 如果已选择了非图片素材
      if (selectedItems.some(item => item.materialType !== 'IMAGE')) {
        return false; // 不允许选择任何其他素材
      }
      
      return true;
    };
    
    const canSelect = isSelectable();
    
    // 获取封面图片URL
    const getCoverImage = () => {
      switch (material.materialType) {
        case 'IMAGE':
          return material.imageInfo?.previewUrl || material.imageInfo?.url;
        case 'VIDEO':
          // 对于视频，优先使用缩略图，其次使用视频URL
          return material.videoInfo?.thumbnailPreviewUrl || 
                 material.videoInfo?.thumbnailUrl || 
                 material.videoInfo?.previewUrl || 
                 material.videoInfo?.url;
        case 'LINK':
          return material.linkInfo?.picPreviewUrl || material.linkInfo?.picUrl;
        case 'MINIPROGRAM':
          return material.miniprogramInfo?.picPreviewUrl || material.miniprogramInfo?.picUrl;
        default:
          return null;
      }
    };

    const coverImageUrl = getCoverImage();
    
    // 获取素材预览内容
    const getPreviewContent = () => {
      if (coverImageUrl) {
        // 对于视频类型，使用视频标签而不是图片标签
        if (material.materialType === 'VIDEO') {
          return (
            <div style={{ height: 180, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', backgroundColor: '#f8f9fa' }}>
              {/* 如果是缩略图URL，使用img标签显示 */}
              {(material.videoInfo?.thumbnailPreviewUrl || material.videoInfo?.thumbnailUrl) && 
               coverImageUrl === (material.videoInfo?.thumbnailPreviewUrl || material.videoInfo?.thumbnailUrl) ? (
                <img
                  src={coverImageUrl}
                  alt={material.name}
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', objectPosition: 'center' }}
                  onError={(e) => {
                    console.error('视频缩略图加载失败:', coverImageUrl, e);
                  }}
                />
              ) : (
                <video
                  src={coverImageUrl}
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', objectPosition: 'center' }}
                  muted
                  preload="metadata"
                  onError={(e) => {
                    console.error('视频加载失败:', coverImageUrl, e);
                  }}
                />
              )}
              {/* 视频播放图标覆盖层 */}
              <div style={{ 
                position: 'absolute', 
                top: 0, 
                left: 0, 
                right: 0, 
                bottom: 0, 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                background: 'rgba(0,0,0,0.3)'
              }}>
                <PlayCircleOutlined style={{ fontSize: '24px', color: '#fff' }} />
              </div>
            </div>
          );
        } else {
          // 其他类型使用图片预览
          return (
            <div style={{ height: 180, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', backgroundColor: '#f8f9fa', padding: '8px', boxSizing: 'border-box' }}>
              <Image
                src={coverImageUrl}
                alt={material.name}
                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', objectPosition: 'center' }}
                preview={false}
                fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RUG8G+ECpYAACAASURBVHhe2bngBbBgIXfAAvCAJQFYcAAXwAJcABfAAVwAB3ABLIALYAFcAAtwASzABbAAF8ACWIALYAEWjsNP0jO"
                onError={(e) => {
                  console.error('图片加载失败:', coverImageUrl, e);
                }}
              />
            </div>
          );
        }
      } else {
        // 无预览图时显示图标和文本
        return (
          <div style={{
            height: 180,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f8f9fa',
            flexDirection: 'column'
          }}>
            <div style={{ marginTop: 8, fontSize: 12, color: '#666', textAlign: 'center', padding: '0 8px' }}>
              {material.materialType === 'TEXT' && material.textContent ? 
                material.textContent.substring(0, 30) + (material.textContent.length > 30 ? '...' : '') :
                material.name
              }
            </div>
          </div>
        );
      }
    };
    
    return (
      <Col span={8} key={material.id}>
        <div style={{
          height: '220px', // 固定高度容器
          marginBottom: 16,  
          display: 'flex',
          flexDirection: 'column',
          border: isSelected ? '2px solid #1890ff' : '1px solid #d9d9d9',
          borderRadius: '8px',
          overflow: 'hidden',
          cursor: canSelect ? 'pointer' : 'not-allowed',
          position: 'relative',
          opacity: canSelect ? 1 : 0.5
        }}
        onClick={() => canSelect && handleMaterialSelect(material)}
        >
          {/* 不可选状态遮罩 */}
          {!canSelect && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              zIndex: 5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontSize: '12px'
            }}>
              不可选
            </div>
          )}
          {/* 选择顺序标识 */}
          {isSelected && selectionOrder > 0 && (
            <div style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              backgroundColor: '#1890ff',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              fontWeight: 'bold',
              zIndex: 10,
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }}>
              {selectionOrder}
            </div>
          )}
          <div style={{ height: '180px', overflow: 'hidden' }}>
            {getPreviewContent()}
          </div>
          <Card
            hoverable={false}
            size="small"
            className={`material-selector-card`}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              border: 'none',
              borderRadius: 0
            }}
            bodyStyle={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              padding: '8px 12px'
            }}
            actions={[]}
          >
          <Card.Meta
            title={
              <Tooltip title={material.name}>
                <div style={{ 
                  overflow: 'hidden', 
                  textOverflow: 'ellipsis', 
                  whiteSpace: 'nowrap',
                  fontSize: 12
                }}>
                  {getMaterialTypeTag(material.materialType)} {material.name}
                </div>
              </Tooltip>
            }
          />
          </Card>
        </div>
      </Col>  
    );
  };


  return (
    <Modal
      title={title}
      open={visible}
      onCancel={onCancel}
      onOk={handleConfirm}
      width={900}
      style={{ top: 20 }}
      bodyStyle={{ height: 600, overflow: 'hidden' }}
      okText={`确定选择${selectedItems.length > 0 ? `(${selectedItems.length})` : ''}`}
      cancelText="取消"
      destroyOnClose
    >
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="素材库" key="library">
          <div style={{ display: 'flex', height: 520 }}>
            {/* 左侧分组树 */}
            <div style={{ width: 200, borderRight: '1px solid #f0f0f0', paddingRight: 16 }}>
              <Tree
                showIcon
                treeData={groupTree}
                onSelect={handleGroupSelect}
                selectedKeys={selectedGroupId !== undefined ? [selectedGroupId] : []}
                height={500}
                className="material-selector-tree"
              />
            </div>

            {/* 右侧素材列表 */}
            <div style={{ flex: 1, paddingLeft: 16, display: 'flex', flexDirection: 'column' }}>
              {/* 类型筛选和搜索 */}
              <div style={{ marginBottom: 16 }}>
                {/* 素材类型标签页 */}
                <div style={{ marginBottom: 16 }}>
                  <Tabs
                    activeKey={(selectedMaterialType || materialType) === null ? 'all' : (selectedMaterialType || materialType)}
                    onChange={(key) => {
                      console.log('MaterialSelector - Tab切换，key:', key);
                      console.log('MaterialSelector - 当前selectedMaterialType:', selectedMaterialType);
                      console.log('MaterialSelector - 当前计算的activeKey:', selectedMaterialType === null ? 'all' : selectedMaterialType);
                      console.log('MaterialSelector - getFilteredMaterialTypes():', getFilteredMaterialTypes());
                      const materialType = key === 'all' ? null : key;
                      console.log('MaterialSelector - 设置materialType为:', materialType);
                      setSelectedMaterialType(materialType);
                    }}
                    type="line"
                    size="small"
                    items={getFilteredMaterialTypes().map(type => ({
                      key: type.value === null ? 'all' : type.value,
                      label: (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          {/* {type.icon} */}
                          {type.label}
                        </span>
                      )
                    }))}
                  />
                </div>
                
                {/* 搜索框和上传按钮 */}
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <Search
                    placeholder="搜索素材名称、描述、文字内容..."
                    allowClear
                    onSearch={handleSearch}
                    size="middle"
                    enterButton
                    style={{ flex: 1 }}
                  />
                  {onUpload && (
                    (selectedMaterialType || materialType) ? (
                      <Button 
                        type="primary" 
                        onClick={() => onUpload(selectedMaterialType || materialType)}
                        size="middle"
                      >
                        {getUploadButtonText()}
                      </Button>
                    ) : (
                      <Dropdown
                        overlay={
                          <Menu>
                            {materialTypes.filter(type => type.value !== null).map(type => (
                              <Menu.Item 
                                key={type.value}
                                onClick={() => onUpload(type.value)}
                              >
                                上传{type.label}
                              </Menu.Item>
                            ))}
                          </Menu>
                        }
                      >
                        <Button type="primary" size="middle">
                          上传素材
                        </Button>
                      </Dropdown>
                    )
                  )}
                </div>
              </div>

              {/* 素材网格 */}
              <div style={{ flex: 1, overflow: 'auto' }}>
                <Spin spinning={loading}>
                  {materials.length > 0 ? (
                    <Row gutter={[16, 16]}>
                      {materials.map(renderMaterialCard)}
                    </Row>
                  ) : (
                    <Empty description="暂无素材" />
                  )}
                </Spin>
              </div>

              {/* 分页 */}
              {pagination.total > 0 && (
                <div style={{ marginTop: 16, textAlign: 'center' }}>
                  <Button
                    disabled={pagination.current === 1}
                    onClick={() => {
                      const effectiveMaterialType = materialType !== undefined ? materialType : selectedMaterialType;
                      loadMaterialsWithType(pagination.current - 1, effectiveMaterialType);
                    }}
                  >
                    上一页
                  </Button>
                  <span style={{ margin: '0 16px' }}>
                    {pagination.current} / {Math.ceil(pagination.total / pagination.pageSize)}
                  </span>
                  <Button
                    disabled={pagination.current >= Math.ceil(pagination.total / pagination.pageSize)}
                    onClick={() => {
                      const effectiveMaterialType = materialType !== undefined ? materialType : selectedMaterialType;
                      loadMaterialsWithType(pagination.current + 1, effectiveMaterialType);
                    }}
                  >
                    下一页
                  </Button>
                </div>
              )}
            </div>
          </div>
        </TabPane>

      </Tabs>
      
      <style jsx>{`
        .ant-card-actions {
          border-top: none;
          padding: 8px 0;
        }
        
        /* 确保网格卡片固定高度对齐 */
        .material-selector-card {
          height: 40px !important; /* 只是底部信息区域的高度 */
        }
        
        /* 确保预览区域完整显示图片和视频 - 更新为新的DOM结构 */
        .material-selector-card + div > div:first-child {
          background-color: #f8f9fa !important;
          overflow: hidden !important;
          height: 180px !important;
          flex-shrink: 0 !important;
        }
        
        /* 针对预览容器内的所有图片和视频使用contain模式 */
        div[style*="height: 180px"] img,
        div[style*="height: 180px"] video {
          max-width: 100% !important;
          max-height: 100% !important;
          width: auto !important;
          height: auto !important;
          object-fit: contain !important;
          object-position: center !important;
          display: block !important;
        }
        
        /* Antd Image组件的contain处理 */
        div[style*="height: 180px"] .ant-image {
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          max-width: 100% !important;
          max-height: 100% !important;
          width: 100% !important;
          height: 100% !important;
        }
        
        div[style*="height: 180px"] .ant-image-img {
          max-width: 100% !important;
          max-height: 100% !important;
          width: auto !important;
          height: auto !important;
          object-fit: contain !important;
          object-position: center !important;
        }
        
        /* 确保所有嵌套容器都遵循contain规则 */
        div[style*="height: 180px"] > div > * {
          max-width: 100% !important;
          max-height: 100% !important;
          object-fit: contain !important;
        }
        
        /* 强制高度约束，确保竖图不会超出容器 */
        div[style*="height: 180px"] {
          height: 180px !important;
          max-height: 180px !important;
        }
        
        /* 特殊处理预览组件确保内容不被裁剪 */
        div[style*="height: 180px"] img[src],
        div[style*="height: 180px"] video[src] {
          max-width: 100% !important;
          max-height: 100% !important;
          object-fit: contain !important;
        }
        
        /* 分组树选中样式 - 绿色主题 - 使用更强的选择器 */
        :global(.material-selector-tree .ant-tree-treenode-selected) {
          background-color: #f6ffed !important;
        }
        
        :global(.material-selector-tree .ant-tree-treenode-selected .ant-tree-node-content-wrapper) {
          background-color: #f6ffed !important;
        }
        
        :global(.material-selector-tree .ant-tree-treenode-selected .ant-tree-title) {
          color: #52c41a !important;
        }
        
        :global(.material-selector-tree .ant-tree-node-selected) {
          background-color: #f6ffed !important;
        }
        
        :global(.material-selector-tree .ant-tree-node-selected .ant-tree-title) {
          color: #52c41a !important;
        }
      `}</style>
    </Modal>
  );
};

export default MaterialSelector; 